import { useQuery } from '@tanstack/react-query';
import { Users, UserCheck, UserX, Clock } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';

export const GuardsWidget = () => {
  const { data: guardsData, isLoading } = useQuery({
    queryKey: ['guards-widget'],
    queryFn: async () => {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, role, license_expiry_date')
        .eq('role', 'guard');

      const { data: activeClockIns } = await supabase
        .from('clock_in_out')
        .select('user_id')
        .eq('status', 'active');

      const activeGuardIds = new Set(activeClockIns?.map(c => c.user_id) || []);
      
      const now = new Date();
      const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

      const expiringSoon = profiles?.filter(p => 
        p.license_expiry_date && 
        new Date(p.license_expiry_date) <= thirtyDaysFromNow &&
        new Date(p.license_expiry_date) > now
      ).length || 0;

      return {
        total: profiles?.length || 0,
        active: activeGuardIds.size,
        offline: (profiles?.length || 0) - activeGuardIds.size,
        expiring: expiringSoon,
        guards: profiles || []
      };
    },
    refetchInterval: 30000,
  });

  if (isLoading) {
    return (
      <Card className="p-6 animate-pulse">
        <div className="h-32 bg-muted rounded"></div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <Users className="w-6 h-6 text-blue-500" />
        <h3 className="text-lg font-semibold">Guards Overview</h3>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">{guardsData?.total || 0}</div>
          <div className="text-sm text-muted-foreground">Total Guards</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">{guardsData?.active || 0}</div>
          <div className="text-sm text-muted-foreground">On Duty</div>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <UserX className="w-4 h-4 text-gray-500" />
            <span className="text-sm">Off Duty</span>
          </div>
          <Badge variant="secondary">{guardsData?.offline || 0}</Badge>
        </div>
        
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-500" />
            <span className="text-sm">License Expiring</span>
          </div>
          <Badge variant={guardsData?.expiring ? "destructive" : "secondary"}>
            {guardsData?.expiring || 0}
          </Badge>
        </div>
      </div>
    </Card>
  );
};